import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Briefcase } from 'lucide-react';

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [label, setLabel] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API}/prestataire-roles`);
      setRoles(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API}/prestataire-roles/${editingRole.id}`, { label }, { withCredentials: true });
        toast.success('Rôle modifié');
      } else {
        await axios.post(`${API}/prestataire-roles`, { label }, { withCredentials: true });
        toast.success('Rôle créé');
      }
      loadRoles();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (role) => {
    if (!role.is_custom) {
      toast.warning('Les rôles par défaut ne peuvent pas être modifiés');
      return;
    }
    setEditingRole(role);
    setLabel(role.label);
    setDialogOpen(true);
  };

  const handleDelete = async (role) => {
    if (!role.is_custom) {
      toast.error('Les rôles par défaut ne peuvent pas être supprimés');
      return;
    }
    if (!window.confirm(`Supprimer le rôle "${role.label}" ?`)) return;
    try {
      await axios.delete(`${API}/prestataire-roles/${role.id}`, { withCredentials: true });
      toast.success('Rôle supprimé');
      loadRoles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setLabel('');
    setEditingRole(null);
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Rôles / Postes</h1>
          <p className="text-stone-600">Gérez les rôles disponibles pour vos prestataires</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau rôle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Modifier le rôle' : 'Créer un nouveau rôle'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nom du rôle *</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  required
                  placeholder="Ex: Assistant Monteur, Éclairagiste, etc."
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-blue-800">
                  💡 Ce rôle apparaîtra automatiquement dans les menus déroulants lors de la création et modification des feuilles de route.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                  {editingRole ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rôle</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-stone-500">
                    Aucun rôle trouvé
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-amber-600" />
                      {role.label}
                    </TableCell>
                    <TableCell>
                      {role.is_custom ? (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                          Personnalisé
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-stone-100 text-stone-600">
                          Par défaut
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {role.is_custom && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(role)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(role)}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-900 mb-2">💡 À savoir</h3>
        <ul className="text-sm text-amber-800 space-y-1">
          <li>• Les rôles par défaut ne peuvent pas être modifiés ou supprimés</li>
          <li>• Vos rôles personnalisés apparaissent automatiquement dans toutes les feuilles de route</li>
          <li>• Vous pouvez créer autant de rôles que nécessaire (ex: Maquilleuse, DJ, etc.)</li>
        </ul>
      </div>
    </div>
  );
};

export default RolesList;
